import type { GateEvidence, StageDetail } from '../api.js';
import { STAGE_LABELS } from '../labels.js';
import { AppLink } from '../link.js';
import { GATE_TARGETS, gateHref, type GateTarget, type SectionKey } from './project-sections.js';
import { useProjectStagesState } from './ProjectStages.js';

/**
 * What this project wants from you, at the top of its page.
 *
 * WHERE IT COMES FROM
 * -------------------
 * Nowhere new. `05`'s gate items are already computed for every stage and
 * already returned by `/v1/projects/:id/stages` — the product has always known
 * that Award is missing a budget and that Site has an open blocker. It just
 * never said so until somebody pressed "Complete" and read the refusal.
 *
 * So this derives NOTHING about the project. It filters the stages that are
 * actually in play to their unsatisfied items and gives each one a link to the
 * screen that fixes it. If this panel and the lifecycle strip ever disagree, the
 * bug is that something here started deciding instead of reporting.
 *
 * WHAT IT ADDS OVER THE PLAIN LIST, AND WHERE THAT COMES FROM
 * ----------------------------------------------------------
 * The list said the stage and the sentence, and a reader still had to open the
 * register to find out whether it mattered today. Each item now carries an
 * `evidence` object from the API (ADR-033) — a count, the earliest date at
 * issue, how many days that is from the PROJECT's today, and an owner where one
 * person genuinely holds all of it. Three things are presentation over that and
 * are decided here rather than on the server:
 *
 *   * **the priority band** — overdue, due soon, open. Deliberately NOT a new
 *     risk rule: the product already has `slaOf`, `riskOf`, `ageOf` and
 *     `computeCoverage`, and a second opinion about how bad a late submittal is
 *     would be exactly the drift those four exist to prevent. This reads the sign
 *     of a day count and nothing more.
 *   * **the words for a date** — "12 days overdue" against a deadline, "open 12
 *     days" against the day a blocker was raised. The difference is
 *     `dateMeans` in `GATE_TARGETS`, because rendering the second as the first
 *     would be wrong in a way that reads perfectly plausibly.
 *   * **the consequence** — one sentence per item, copy rather than data,
 *     living beside the link it belongs to.
 *
 * EVIDENCE CAN BE ABSENT, AND THAT IS NOT AN ERROR
 * ------------------------------------------------
 * Three separate things produce a missing count: a yes/no item with nothing to
 * count, a satisfied item, and **a caller who may not read the records it comes
 * from**. A Quantity Surveyor holds `stage: view` and no `deliverable` grant at
 * all, so they see what Design wants and not how many drawings are late. The row
 * degrades to what it used to be, which is still useful, and nothing renders a
 * dash where a figure would go.
 *
 * WHICH STAGES COUNT
 * ------------------
 * `active`, `blocked` and `at_risk` — the ones somebody is working on. The
 * stages OVERLAP by design (ADR-002), so this is usually two or three of them
 * at once and never "the current stage".
 *
 * A `not_started` stage is deliberately excluded. Every one of its items is
 * unsatisfied by definition, and a handover checklist demanded on day one is
 * the noise that teaches people to stop reading the panel.
 *
 * WHEN IT SAYS NOTHING
 * --------------------
 * When nothing is outstanding it says so in one line rather than disappearing.
 * A panel that vanishes cannot be told apart from a panel that failed to load,
 * and "nothing is waiting on you" is a genuinely useful sentence on a job.
 */

interface Props {
  readonly projectId: string;
}

/** The stage statuses somebody is actually working on. */
const IN_PLAY: ReadonlySet<string> = new Set(['active', 'blocked', 'at_risk']);

/**
 * Inside this many days, a deadline is "due soon" rather than merely open.
 *
 * Seven, to match a working week — the horizon somebody looking at this on a
 * Monday can actually do something about. It is a PRESENTATION threshold and
 * deliberately not one of the Pulse's: `slaOf` uses two days for a submittal
 * because that is a rule about submittals, and copying it here would make this
 * panel a second opinion about procurement risk rather than a view of the gate.
 */
const DUE_SOON_DAYS = 7;

/**
 * The three bands, worst first. Each carries a symbol AND a word, because the
 * design system forbids saying anything by colour alone — and a register that
 * survives a projector and a bright site is the reason.
 */
export type Band = 'overdue' | 'due_soon' | 'open';

interface BandDef {
  readonly key: Band;
  readonly symbol: string;
  readonly label: string;
  /** What the group heading says under the label. */
  readonly gist: string;
}

export const BANDS: readonly BandDef[] = [
  /*
   * "Late or stopped", not "Past its date" — measured against the seeded project
   * and corrected. The band legitimately holds two different things: a deadline
   * that has gone by, and an open blocker, which has no deadline at all. A
   * heading that said "past its date" above a blocker raised this morning was
   * describing one of its two members and contradicting the other.
   */
  { key: 'overdue', symbol: '■', label: 'Late or stopped', gist: 'already late, or holding work up now' },
  { key: 'due_soon', symbol: '▲', label: 'Due this week', gist: 'still time, but not much' },
  { key: 'open', symbol: '●', label: 'Open', gist: 'outstanding with no date pressing' },
];

const BAND_BY_KEY: Readonly<Record<Band, BandDef>> = Object.fromEntries(
  BANDS.map((band) => [band.key, band]),
) as Readonly<Record<Band, BandDef>>;

/**
 * Which band an item sits in.
 *
 * Reads the SIGN of `evidence.days` and nothing else, so it cannot disagree with
 * the gate that produced it:
 *
 *   * a `since` date — one item, the blockers — is always worst. Something is
 *     stopped now, and how long it has been stopped makes that worse rather than
 *     better;
 *   * a deadline already past is worst;
 *   * a deadline inside `DUE_SOON_DAYS` is the middle band;
 *   * no date at all is open. NOT the bottom by default: an item with no date is
 *     not necessarily minor, it is only undated, and the band says exactly that.
 */
export function bandOf(evidence: GateEvidence | undefined, target: GateTarget | undefined): Band {
  const days = evidence?.days;
  if (days === undefined) return 'open';
  if (target?.dateMeans === 'since') return 'overdue';
  if (days < 0) return 'overdue';
  return days <= DUE_SOON_DAYS ? 'due_soon' : 'open';
}

/** "3 drawings", "1 drawing", and nothing at all without a count or a noun. */
export function countWords(
  count: number | undefined,
  noun: readonly [string, string] | undefined,
): string | null {
  if (count === undefined || count <= 0 || !noun) return null;
  return `${count} ${count === 1 ? noun[0] : noun[1]}`;
}

/**
 * What a date means in words.
 *
 * A `since` date counts UP — "open 12 days" — and a deadline counts down and
 * then goes negative. "Due today" and "raised today" are both worth their own
 * sentence: "0 days" is a figure a reader has to translate.
 */
export function timingWords(
  evidence: GateEvidence | undefined,
  target: GateTarget | undefined,
): string | null {
  const days = evidence?.days;
  if (days === undefined) return null;

  if (target?.dateMeans === 'since') {
    const age = Math.abs(days);
    if (age === 0) return 'raised today';
    return `open ${age} ${age === 1 ? 'day' : 'days'}`;
  }

  if (days < 0) {
    const late = Math.abs(days);
    return `${late} ${late === 1 ? 'day' : 'days'} overdue`;
  }
  if (days === 0) return 'due today';
  return `due in ${days} ${days === 1 ? 'day' : 'days'}`;
}

interface Wanted {
  readonly stageKey: string;
  readonly stageLabel: string;
  readonly stageStatus: string;
  readonly itemKey: string;
  readonly label: string;
  readonly section: SectionKey | null;
  readonly register: string | null;
  readonly action: string | null;
  readonly consequence: string | null;
  readonly band: Band;
  readonly count: string | null;
  readonly timing: string | null;
  readonly owner: string | null;
}

/** The unsatisfied gate items of every stage in play, worst band first. */
export function outstandingItems(stages: readonly StageDetail[]): readonly Wanted[] {
  const wanted: Wanted[] = [];
  for (const stage of stages) {
    if (!IN_PLAY.has(stage.status)) continue;
    for (const item of stage.gate.items) {
      if (item.status !== 'unsatisfied') continue;
      const target = GATE_TARGETS[item.key];
      wanted.push({
        stageKey: stage.stage_key,
        stageLabel: STAGE_LABELS[stage.stage_key] ?? stage.stage_key,
        stageStatus: stage.status,
        itemKey: item.key,
        label: item.label,
        section: target?.section ?? null,
        register: target?.register ?? null,
        action: target?.action ?? null,
        consequence: target?.consequence ?? null,
        band: bandOf(item.evidence, target),
        count: countWords(item.evidence?.count, target?.noun),
        timing: timingWords(item.evidence, target),
        owner: item.evidence?.owner ?? null,
      });
    }
  }
  return wanted;
}

/*
 * `inBand` used to split the items into three headed groups. The queue sorts
 * instead of grouping, so it went with the groups — a helper kept "in case" is
 * a helper the next reader has to work out is dead.
 */

/**
 * ONE ROW OF THE QUEUE.
 *
 * A table row, not a card. The brief for this product asks for an actionable
 * work queue — priority, record, reason, owner, due date, consequence, action —
 * and those are columns. The previous shape was a tinted block per item inside a
 * tinted group, which is how a list of jobs ends up looking like a dashboard.
 *
 * Every cell carries `data-label`, so the register collapses to cards on a
 * phone with its column names intact, exactly as the other registers do.
 */
function ItemRow({ item, projectId }: { readonly item: Wanted; readonly projectId: string }): JSX.Element {
  const band = BAND_BY_KEY[item.band];

  return (
    <tr className={`needs-row band-${item.band}`}>
      <td data-label="Priority">
        {/*
         * Symbol AND word. The design system forbids saying anything by colour
         * alone, and this is the column somebody scans down first.
         */}
        <span className="needs-sev" aria-hidden="true">
          {band.symbol}
        </span>{' '}
        <span className="needs-priority">{band.label}</span>
      </td>

      <td data-label="Needs doing">
        <span className="needs-what">{item.label}</span>
        <span className="needs-stage">{item.stageLabel}</span>
      </td>

      {/*
       * Count, owner and due are their own columns so they line up down the
       * register — that is the whole reason a queue is a table. Each can be
       * ABSENT: a caller who may not read the underlying records gets no figures
       * at all (ADR-033), and an em dash is the honest placeholder for "nothing
       * to say" rather than a zero.
       */}
      <td data-label="How many">{item.count ?? '—'}</td>
      <td data-label="Owner">{item.owner ?? '—'}</td>
      <td data-label="Due" className={item.band === 'overdue' ? 'needs-timing-late' : undefined}>
        {item.timing ?? '—'}
      </td>
      <td data-label="Why it matters" className="needs-why">
        {item.consequence ?? '—'}
      </td>

      <td>
        {item.section && item.action ? (
          /*
            `gateHref`, NOT `sectionHref`. The register carried on every gate
            target has to end up in the URL as an anchor, or an item whose
            screen is the OVERVIEW links to the page the reader is already
            standing on — and the link does nothing at all when pressed. Every
            Award item is in exactly that position.
          */
          <AppLink
            className="needs-go"
            to={gateHref(projectId, { section: item.section, register: item.register } as GateTarget)}
          >
            {/* The words are the destination, so the column reads as a list of
                places rather than nine identical "Go to" links. */}
            {item.action}
            <span className="visually-hidden"> — for {item.label}</span>
          </AppLink>
        ) : (
          // Every key in `stages.ts` has a target and a test says so. This is for
          // a server newer than this bundle: naming the item beats a link to
          // nowhere.
          <span className="hint">No screen yet</span>
        )}
      </td>
    </tr>
  );
}

export function NeedsYouNow({ projectId }: Props): JSX.Element {
  const { state } = useProjectStagesState(projectId);

  const head = (
    <h3 className="sub-head" id="needs-head">
      Needs you now
    </h3>
  );

  if (state.status === 'loading') {
    return (
      <section className="needs" aria-labelledby="needs-head">
        {head}
        <p aria-live="polite">Checking what is outstanding…</p>
      </section>
    );
  }

  // A consultant may open the project and not see its stages. Saying "nothing
  // is outstanding" to them would be a claim about records they cannot read.
  if (state.status === 'not_permitted') {
    return (
      <section className="needs" aria-labelledby="needs-head">
        {head}
        <p className="hint">Your role does not include this project&rsquo;s stages.</p>
      </section>
    );
  }

  if (state.status === 'failed') {
    return (
      <section className="needs" aria-labelledby="needs-head">
        {head}
        <div className="alert" role="alert">
          <span>{state.message}</span>
        </div>
      </section>
    );
  }

  const items = outstandingItems(state.stages);
  const blocked = state.stages.filter((s) => s.status === 'blocked');
  const running = state.stages.filter((s) => s.status === 'active' || s.status === 'at_risk');

  if (items.length === 0 && blocked.length === 0) {
    return (
      <section className="needs needs-clear" aria-labelledby="needs-head">
        {head}
        <p className="needs-verdict">
          <span className="needs-symbol" aria-hidden="true">
            ✓
          </span>{' '}
          <strong>Nothing is outstanding</strong> on{' '}
          {running.length === 0
            ? 'this project'
            : `${running.map((s) => STAGE_LABELS[s.stage_key] ?? s.stage_key).join(' and ')}`}
          .
        </p>
        {running.length === 0 && (
          <p className="hint">
            No stage is running yet. Start one from the lifecycle below and its checks appear here.
          </p>
        )}
      </section>
    );
  }

  const pressing = items.filter((item) => item.band !== 'open').length;

  /*
   * Worst first, then in stage order.
   *
   * The bands used to be separate headed groups; sorting instead of grouping is
   * what turns this from a set of panels into one queue you read top to bottom.
   * Stage order inside a band is the order the work happens in, so a late
   * drawing sits above the material it is holding up.
   */
  const order: Record<Band, number> = { overdue: 0, due_soon: 1, open: 2 };
  const queue = [...items].sort((a, b) => order[a.band] - order[b.band]);

  return (
    <section className="needs needs-wanting" aria-labelledby="needs-head">
      <div className="section-head sub-head-row">
        {head}
        <span className="needs-count">
          {items.length} outstanding
          <span className="visually-hidden"> across the stages that are running</span>
          {pressing > 0 && (
            <>
              <span aria-hidden="true"> · </span>
              <strong className="needs-count-pressing">{pressing} dated</strong>
            </>
          )}
        </span>
      </div>

      {blocked.length > 0 && (
        <p className="needs-blocked">
          <span className="needs-symbol" aria-hidden="true">
            ■
          </span>{' '}
          <strong>
            {blocked.map((s) => STAGE_LABELS[s.stage_key] ?? s.stage_key).join(', ')}{' '}
            {blocked.length === 1 ? 'is' : 'are'} blocked.
          </strong>{' '}
          Unblock from the lifecycle below.
        </p>
      )}

      <table className="grid needs-queue">
        <caption className="visually-hidden">
          What this project needs from you, most urgent first
        </caption>
        <thead>
          <tr>
            <th scope="col">Priority</th>
            <th scope="col">Needs doing</th>
            <th scope="col">How many</th>
            <th scope="col">Owner</th>
            <th scope="col">Due</th>
            <th scope="col">Why it matters</th>
            <th scope="col">
              <span className="visually-hidden">Where to fix it</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {queue.map((item) => (
            <ItemRow key={`${item.stageKey}:${item.itemKey}`} item={item} projectId={projectId} />
          ))}
        </tbody>
      </table>

      <p className="hint">
        These are the checks the stage runs when you press Complete. A stage can still be completed
        over them, with a written reason that is kept.
      </p>
    </section>
  );
}